import { Group } from 'grommet-icons';
import React, { useMemo, useState } from 'react';

import { SubHeader } from '../../components';
import { useUsers } from '../../context/users';
import { User, Lead, LeadStatus, Unit } from '../../context/model/types';
import UserModal from '../../components/Modals/UserModal/UserModal';
import LeadModal from '../../components/Modals/LeadModal';

type Tab = 'customers' | 'leads';
type SortDir = 'asc' | 'desc';

const STATUS_COLORS: Record<LeadStatus, { bg: string; fg: string }> = {
  new:       { bg: '#E5E7EB', fg: '#374151' },
  contacted: { bg: '#DBEAFE', fg: '#1E40AF' },
  responded: { bg: '#FEF3C7', fg: '#92400E' },
  converted: { bg: '#D1FAE5', fg: '#065F46' },
};

const SOURCE_LABELS: Record<string, string> = {
  google_ads: 'Google Ads',
  reddit: 'Reddit',
  referral: 'Referral',
  school_forum: 'School forum',
  found_online: 'Found online',
  other: 'Other',
};

// A tenant assigned to a unit is Active. Others may have moved out OR not
// finished signing up — we can't tell which, so only "Active" is labelled.
const isActive = (u: User): boolean => !!(u.unitID && String(u.unitID).trim());

const Customers = (): JSX.Element => {
  const { users, leads, units } = useUsers();
  const [tab, setTab] = useState<Tab>('customers');
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);

  const filteredCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = q
      ? users.filter(u =>
          u.name?.toLowerCase().includes(q) ||
          u.id?.toLowerCase().includes(q) ||
          u.phone?.toLowerCase().includes(q)
        )
      : users;
    if (activeOnly) {
      base = base.filter(isActive);
    }
    return [...base].sort((a, b) => {
      const av = a.name?.toLowerCase() ?? '';
      const bv = b.name?.toLowerCase() ?? '';
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [users, search, sortDir, activeOnly]);

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? leads.filter(l =>
          l.name?.toLowerCase().includes(q) ||
          l.phone?.toLowerCase().includes(q) ||
          l.notes?.toLowerCase().includes(q)
        )
      : leads;
    return [...base].sort((a, b) => {
      const av = a.dateCreated ?? '';
      const bv = b.dateCreated ?? '';
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [leads, search, sortDir]);

  return (
    <>
      <SubHeader title="Customers" icon={<Group />} />
      <div style={{ padding: '16px 24px', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        {/* tab toggle */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderBottom: '1px solid #E5E7EB' }}>
          <TabButton active={tab === 'customers'} onClick={() => setTab('customers')}>
            Customers <span style={countStyle}>{users.length}</span>
          </TabButton>
          <TabButton active={tab === 'leads'} onClick={() => setTab('leads')}>
            Leads <span style={countStyle}>{leads.length}</span>
          </TabButton>
        </div>

        {/* toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <input
            type="search"
            placeholder={tab === 'customers' ? 'Search name, email, phone…' : 'Search name, phone, notes…'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB',
              fontSize: 13, width: 260, outline: 'none',
            }}
          />
          <span style={{ fontSize: 13, color: '#6B7280' }}>
            {tab === 'customers'
              ? `${filteredCustomers.length} customer${filteredCustomers.length !== 1 ? 's' : ''}`
              : `${filteredLeads.length} lead${filteredLeads.length !== 1 ? 's' : ''}`}
          </span>
          {tab === 'customers' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={() => setActiveOnly(v => !v)}
                style={{ accentColor: '#7B1FA2', cursor: 'pointer' }}
              />
              <span style={{
                display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                fontSize: 11, fontWeight: 600, backgroundColor: '#D1FAE5', color: '#065F46',
              }}>
                Active only
              </span>
            </label>
          )}
          {tab === 'leads' && (
            <button
              onClick={() => setSelectedLead({ id: '' } as Lead)}
              style={{
                marginLeft: 'auto',
                padding: '8px 14px', borderRadius: 6, border: 'none',
                background: '#7B1FA2', color: '#fff',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              + Add lead
            </button>
          )}
        </div>

        {/* table */}
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #E5E7EB' }}>
          {tab === 'customers' ? (
            <CustomersTable
              users={filteredCustomers}
              units={units}
              sortDir={sortDir}
              setSortDir={setSortDir}
              onRowClick={setSelectedUser}
            />
          ) : (
            <LeadsTable
              leads={filteredLeads}
              sortDir={sortDir}
              setSortDir={setSortDir}
              onRowClick={setSelectedLead}
            />
          )}
        </div>
      </div>

      {selectedUser && (
        <UserModal user={selectedUser} setShow={() => setSelectedUser(null)} type="customer" />
      )}
      {selectedLead && (
        <LeadModal lead={selectedLead} setShow={() => setSelectedLead(null)} />
      )}
    </>
  );
};

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active, onClick, children,
}) => (
  <button
    onClick={onClick}
    style={{
      padding: '10px 16px',
      border: 'none',
      borderBottom: active ? '2px solid #7B1FA2' : '2px solid transparent',
      background: 'transparent',
      fontSize: 14,
      fontWeight: active ? 600 : 500,
      color: active ? '#111827' : '#6B7280',
      cursor: 'pointer',
      marginBottom: -1,
    }}
  >
    {children}
  </button>
);

const countStyle: React.CSSProperties = {
  marginLeft: 6,
  fontSize: 12,
  color: '#9CA3AF',
  fontWeight: 500,
};

// ── Customers table ──────────────────────────────────────────────────────────

const CustomersTable: React.FC<{
  users: User[];
  units: Unit[];
  sortDir: SortDir;
  setSortDir: React.Dispatch<React.SetStateAction<SortDir>>;
  onRowClick: (u: User) => void;
}> = ({ users, units, sortDir, setSortDir, onRowClick }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
    <thead>
      <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
        <th
          onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          style={thStyle({ sortable: true })}
        >
          Name {sortDir === 'asc' ? '↑' : '↓'}
        </th>
        <th style={thStyle({})}>Status</th>
        <th style={thStyle({})}>Email</th>
        <th style={thStyle({})}>Phone</th>
        <th style={thStyle({})}>Plan</th>
        <th style={thStyle({})}>Unit</th>
      </tr>
    </thead>
    <tbody>
      {users.length === 0 && (
        <tr>
          <td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
            No customers found
          </td>
        </tr>
      )}
      {users.map(user => {
        // user.unitID holds the Unit's primary key — resolve it to the readable label.
        const unit = units.find(u => String(u.id) === String(user.unitID));
        const unitLabel = unit?.unitID || (user.unitID ? user.unitID : '');
        // A tenant assigned to a unit is Active; move-out clears unitID -> Moved Out.
        const active = !!(user.unitID && String(user.unitID).trim());
        return (
          <tr
            key={user.id}
            onClick={() => onRowClick(user)}
            style={{ borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F9FAFB')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
          >
            <td style={tdStyle()}>{user.name || '—'}</td>
            <td style={tdStyle()}>
              {active ? (
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                  fontSize: 11, fontWeight: 500,
                  backgroundColor: '#D1FAE5', color: '#065F46',
                }}>
                  Active
                </span>
              ) : (
                <span style={{ color: '#9CA3AF' }}>—</span>
              )}
            </td>
            <td style={{ ...tdStyle(), color: '#6B7280' }}>{user.id}</td>
            <td style={tdStyle()}>{user.phone || '—'}</td>
            <td style={tdStyle()}>{user.plan?.size ? user.plan.size.replace(/x/i, '×') : '—'}</td>
            <td style={tdStyle()}>{unitLabel || '—'}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

// ── Leads table ──────────────────────────────────────────────────────────────

const LeadsTable: React.FC<{
  leads: Lead[];
  sortDir: SortDir;
  setSortDir: React.Dispatch<React.SetStateAction<SortDir>>;
  onRowClick: (l: Lead) => void;
}> = ({ leads, sortDir, setSortDir, onRowClick }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
    <thead>
      <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
        <th
          onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          style={thStyle({ sortable: true })}
        >
          Date {sortDir === 'asc' ? '↑' : '↓'}
        </th>
        <th style={thStyle({})}>Name</th>
        <th style={thStyle({})}>Phone</th>
        <th style={thStyle({})}>Last Contacted</th>
        <th style={thStyle({})}>Follow Up</th>
        <th style={thStyle({})}>Source</th>
        <th style={thStyle({})}>Anticipated</th>
        <th style={thStyle({})}>Status</th>
        <th style={thStyle({})}>Notes</th>
      </tr>
    </thead>
    <tbody>
      {leads.length === 0 && (
        <tr>
          <td colSpan={9} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
            No leads found
          </td>
        </tr>
      )}
      {leads.map(lead => {
        const colors = STATUS_COLORS[lead.status] ?? STATUS_COLORS.new;
        const sourceLabel = lead.source === 'other'
          ? (lead.sourceCustom || 'Other')
          : (lead.source ? SOURCE_LABELS[lead.source] : '—');
        return (
          <tr
            key={lead.id}
            onClick={() => onRowClick(lead)}
            style={{ borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F9FAFB')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
          >
            <td style={tdStyle()}>{lead.dateCreated || '—'}</td>
            <td style={tdStyle()}>{lead.name || '—'}</td>
            <td style={tdStyle()}>{lead.phone || '—'}</td>
            <td style={{ ...tdStyle(), color: '#6B7280' }}>
              {lead.lastContacted || '—'}
              {lead.contactMethod && (
                <span style={{ color: '#9CA3AF', marginLeft: 6 }}>({lead.contactMethod})</span>
              )}
            </td>
            <td style={tdStyle()}>{lead.followUpAt || '—'}</td>
            <td style={tdStyle()}>{sourceLabel}</td>
            <td style={tdStyle()}>{lead.anticipatedDate || '—'}</td>
            <td style={tdStyle()}>
              <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 500,
                backgroundColor: colors.bg,
                color: colors.fg,
              }}>
                {lead.status}
              </span>
            </td>
            <td style={{ ...tdStyle(), color: '#6B7280', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lead.notes ?? ''}>
              {lead.notes || '—'}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const thStyle = ({ sortable }: { sortable?: boolean }): React.CSSProperties => ({
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#374151',
  whiteSpace: 'nowrap',
  cursor: sortable ? 'pointer' : 'default',
  userSelect: 'none',
});

const tdStyle = (): React.CSSProperties => ({
  padding: '10px 14px',
  color: '#111827',
  whiteSpace: 'nowrap',
});

export default Customers;
